<template>
  <div
    :class="['corporate-dropdown', 'w-full', containerClass]"
    :style="containerStyle"
    :dir="dirAttr"
  >
    <span class="p-float-label">
      <Dropdown
        filter
        :options="corporates"
        optionLabel="name"
        optionValue="id"
        :placeholder="usePlaceholder ? label : ' '"
        v-model="model"
        :class="dropdownClass"
        :style="dropdownStyleWithDefault"
      />
      <label>{{ label }}</label>
    </span>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import Dropdown from "primevue/dropdown";
import { useI18n } from "vue-i18n";

const props = defineProps({
  label: { type: String, default: "" },
  dropdownClass: { type: String, default: "w-full" },
  dropdownStyle: { type: [String, Object], default: undefined },
  containerStyle: { type: [String, Object], default: undefined },
  containerClass: { type: String, default: undefined },
  autoDir: { type: Boolean, default: true },
  usePlaceholder: { type: Boolean, default: false },
});
const model = defineModel();

const store = useStore();
const { locale } = useI18n();

const corporates = computed(() => {
  const data = store.getters["corporates/getCorporatesDrop"];
  return Array.isArray(data) ? data : data?.corporates || [];
});

const dropdownStyleWithDefault = computed(() => {
  if (!props.dropdownStyle) return "width: 100%";
  if (typeof props.dropdownStyle === "string") {
    return `width: 100%; ${props.dropdownStyle}`;
  }
  return { width: "100%", ...props.dropdownStyle };
});

const isRtl = computed(() => {
  if (!props.autoDir) return false;
  const val = (locale?.value || "").toLowerCase();
  return val.startsWith("ar");
});

const dirAttr = computed(() => (isRtl.value ? "rtl" : "ltr"));

onMounted(() => {
  if (!corporates.value || corporates.value.length === 0) {
    store.dispatch("corporates/getCorporatesDrop");
  }
});

watch(
  corporates,
  (newVal) => {
    if (newVal && newVal.length > 0 && !model.value) {
      model.value = newVal[0].id;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.corporate-dropdown {
  width: 250px;
  min-width: 200px;
  max-width: 100%;
}

.corporate-dropdown,
.corporate-dropdown .p-dropdown,
.corporate-dropdown label {
  font-size: 14px !important;
}

/* RTL/LTR adjustments */
.corporate-dropdown[dir='rtl'] .p-dropdown {
  direction: rtl;
  text-align: right;
}
.corporate-dropdown[dir='ltr'] .p-dropdown {
  direction: ltr;
  text-align: left;
}
/* LTR clear icon centering and label padding for space of icons (right side) */
.corporate-dropdown[dir='ltr'] .p-dropdown .p-dropdown-clear-icon {
  right: 2.25rem;
  left: auto;
  top: 50%;
  transform: translateY(-50%);
}
.corporate-dropdown[dir='ltr'] .p-dropdown .p-dropdown-label {
  padding-left: 0.75rem;
  padding-right: 3rem;
  text-align: left;
}
/* Move trigger and clear icon to the left in RTL */
.corporate-dropdown[dir='rtl'] .p-dropdown .p-dropdown-trigger {
  left: 0.5rem;
  right: auto;
}
.corporate-dropdown[dir='rtl'] .p-dropdown .p-dropdown-clear-icon {
  left: 2.25rem !important; /* keep some space from trigger */
  right: auto !important;
  top: 50%;
  transform: translateY(-50%);
}
/* Ensure label/text have proper padding in RTL to avoid overlap */
.corporate-dropdown[dir='rtl'] .p-dropdown .p-dropdown-label {
  padding-right: 0.75rem;
  padding-left: 3rem; /* space for clear + trigger */
  text-align: right;
}
/* PrimeVue float label position flip for RTL */
.corporate-dropdown[dir='rtl'] .p-float-label > label {
  right: 0.75rem;
  left: auto;
}
.corporate-dropdown[dir='ltr'] .p-float-label > label {
  left: 0.75rem;
  right: auto;
}
</style>
